/**
 * 将展会语义列表 URL 中的遗留分页段 `/p{N}`（如 /p1、/p12）规范为 **`/p-{N}`**，与文档约定一致（301）。
 * **`/p-1`** 重定向为去掉该段（第 1 页不写 path 分页段）。
 * 将 path 中的 **m-{月}** 迁移为 query **?m=**（301）——**仅非白名单**组合页。
 * pathname 末尾补 **`/`** 的 301 在父页 §1.3 404 守卫通过后执行（见 fair/list/index.vue）。
 * 仅限 meta.fairSemanticList 的路由，避免误伤其它站点路径。
 * Location 使用当前请求协议+host 绝对 URL。
 */
import { buildAbsoluteRedirectHref } from '~/utils/absoluteRedirectLocation'
import {
  fairSemanticListPathNeedsPaginationNormalize,
  fairSemanticListRedirectPathname,
  normalizeFairSemanticListPaginationPath,
  shouldStripMonthSegmentToQuery,
  stripFairSemanticPathMonthSegment
} from '~/utils/fairSemanticListing'

export default defineNuxtRouteMiddleware(to => {
  const pathOnly = to.path.replace(/\/+/g, '/')

  /** 旧 nationwide 月份：/m-7、/m-7/p-2 → /all/m-7/ 或 /all/m-7/p-2/ */
  const legacyMonth = /^\/m-(\d+)(?:\/p-(\d+))?\/?$/.exec(pathOnly)
  if (legacyMonth) {
    const m = legacyMonth[1]
    const p = legacyMonth[2]
    const pg = p ? Math.max(1, Math.floor(Number(p)) || 1) : 1
    const target = pg > 1 ? `/all/m-${m}/p-${pg}` : `/all/m-${m}`
    return navigateTo(
      buildAbsoluteRedirectHref(fairSemanticListRedirectPathname(target), to.query as Record<string, unknown>, to.hash),
      { redirectCode: 301, external: true }
    )
  }

  const onSemanticFairList = to.matched.some(
    record => !!(record.meta as Record<string, unknown>).fairSemanticList
  )
  if (!onSemanticFairList) return

  const { path: withoutMonth, month } = stripFairSemanticPathMonthSegment(pathOnly)
  if (month > 0 && withoutMonth !== pathOnly && shouldStripMonthSegmentToQuery(pathOnly)) {
    const query = { ...to.query }
    if (query.m == null || query.m === '') query.m = String(month)
    return navigateTo(
      buildAbsoluteRedirectHref(
        fairSemanticListRedirectPathname(withoutMonth),
        query as Record<string, unknown>,
        to.hash
      ),
      { redirectCode: 301, external: true }
    )
  }

  if (fairSemanticListPathNeedsPaginationNormalize(pathOnly)) {
    const normalized = normalizeFairSemanticListPaginationPath(pathOnly)
    return navigateTo(
      buildAbsoluteRedirectHref(
        fairSemanticListRedirectPathname(normalized),
        to.query as Record<string, unknown>,
        to.hash
      ),
      { redirectCode: 301, external: true }
    )
  }
})
